import React, { MouseEvent, ReactNode, useEffect, useState } from 'react';

import { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';

import { LinearProgress } from '@mui/material';
import {
  DataGridPro,
  DataGridProProps,
  GridRowId,
  GridSelectionModel,
  GridValidRowModel,
  nlNL
} from '@mui/x-data-grid-pro';

import { ConfirmationDialog } from 'components/shared';

import DataGridHeader, {
  DataGridHeaderPortal
} from './components/DataGridHeader';
import GridContainer from './components/styled/GridContainer';
import GridStyleWrapper from './components/styled/GridStyleWrapper';

import NoRowsOverlay from './NoRowsOverlay';
import { useGetCustomDataGridColumnsToRender } from './hooks/useGetCustomDataGridColumnsToRender';
import { OverridableStringUnion } from '@mui/types';
import { ButtonPropsColorOverrides } from '@mui/material/Button/Button';

export type AdditionalButton = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  startIcon?: ReactNode;
  color?: OverridableStringUnion<
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning',
    ButtonPropsColorOverrides
  >;
};

export type DataGridActionClick<T> = (
  id: string | number,
  row?: T,
  props?: any
) => void;

export type DataGridActionsFn<T = any> = (
  id: string | number,
  row?: T
) => DataGridAction<T>[];

export interface DataGridAction<T = any> {
  icon: IconDefinition | ((id: string | number, row?: T) => IconDefinition);
  onClick: DataGridActionClick<T>;
  tooltipTitle: string | ((id: string | number, row?: T) => string);
  disabled?: boolean | ((id: string | number, row?: T) => boolean);
  hidden?: boolean | ((id: string | number, row?: T) => boolean);
}

interface CommonProps<T extends GridValidRowModel> extends DataGridProProps<T> {
  onAddClick?: (args?) => void;
  onAddClickTitle?: string;
  onDeleteClick?: (idsList: GridRowId[]) => void;
  onEditClick?: (id: string | number, row?: T) => void;
  onCopyClick?: (id: string | number, row?: T) => void;
  getTreeDataPath?: (row: T) => string[];
  onViewClick?: (
    id: string | number,
    row?: T,
    event?: MouseEvent<HTMLButtonElement>
  ) => void;
  onSelectionChange?: (id: (string | number)[], rows?: T[]) => void;
  isWithViewSwitcher?: boolean;
  shouldRenderToolBar?: boolean;
  additionalButtons?: AdditionalButton[];
  customActions?: DataGridAction<T>[] | DataGridActionsFn<T>;
  actionsThreshold?: number;
  addRowDialog?: ReactNode;
  actionsColumnWidth?: number;
  disableActions?: boolean;
  confirmationTitle?: string;
  confirmationBody?: string;
  containerHeight?: string;
  footer?: JSX.Element | null;
  headerContent?: React.ReactNode;
  translationKey?: string;
  canBeEditedProp?: string;
  canBeDeletedProp?: string;
  customSelectionModel?: GridSelectionModel;
}

export type CheckBoxSelectionProps =
  | {
      checkboxSelection?: true;
      title?: never;
    }
  | {
      checkboxSelection?: false;
      title?: string;
    };

export type CustomDataGridProps<T extends GridValidRowModel = any> =
  CommonProps<T> & CheckBoxSelectionProps;

const CustomDataGrid = <T extends GridValidRowModel = any>({
  rows,
  columns,
  loading,
  title,
  getTreeDataPath,
  groupingColDef,
  onAddClick,
  onAddClickTitle,
  onDeleteClick,
  onEditClick,
  onCopyClick,
  onViewClick,
  customActions,
  actionsThreshold = 3,
  addRowDialog,
  additionalButtons,
  checkboxSelection,
  confirmationTitle,
  confirmationBody,
  customSelectionModel,
  disableActions,
  footer = null,
  initialState,
  pagination,
  rowsPerPageOptions,
  onPageSizeChange,
  onSelectionChange,
  pageSize,
  components,
  autoHeight = true,
  shouldRenderToolBar = false,
  headerContent,
  containerHeight,
  translationKey,
  canBeEditedProp,
  canBeDeletedProp,
  ...rest
}: CustomDataGridProps<T>) => {
  const { t, i18n } = useTranslation('common');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [rowIdToDelete, setRowIdToDelete] = useState<number | string | null>(
    null
  );
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);

  const [deleteSelection, setDeleteSelection] = useState(false);
  const [treeDataState, setTreeDataState] = useState<boolean>(false);

  const handleDeleteClick = (id: string | number) => {
    setRowIdToDelete(id);
    setShowDeleteConfirmation(true);
  };

  const columnsToRender = useGetCustomDataGridColumnsToRender(
    onViewClick,
    onCopyClick,
    onEditClick,
    onDeleteClick,
    handleDeleteClick,
    customActions,
    columns,
    actionsThreshold,
    disableActions,
    canBeEditedProp,
    canBeDeletedProp
  );

  const onDeleteConfirmation = (isConfirmed: boolean) => {
    if (isConfirmed && onDeleteClick) {
      if (deleteSelection) {
        onDeleteClick(selectionModel);
        setDeleteSelection(false);
      } else if (rowIdToDelete) {
        onDeleteClick([rowIdToDelete]);
        setRowIdToDelete(null);
      }
    }
    setShowDeleteConfirmation(false);
  };

  const onSelectionModelChange = (selectionModel: GridSelectionModel) => {
    setSelectionModel(selectionModel);
    onSelectionChange?.(
      selectionModel,
      rows.filter((row) => selectionModel.includes(row.id))
    );
  };

  function noRows() {
    return <NoRowsOverlay translationKey={translationKey} />;
  }

  const DataGridHeaderComponent = (
    <DataGridHeader
      onAddClickTitle={onAddClickTitle}
      additionalButtons={additionalButtons}
      onAddClick={onAddClick}
      onDeleteClick={onDeleteClick}
      title={title}
      selectionModel={selectionModel}
      setShowDeleteConfirmation={setShowDeleteConfirmation}
      setDeleteSelection={setDeleteSelection}
      disableActions={disableActions}
      checkboxSelection={checkboxSelection}
      headerContent={headerContent}
      {...(!shouldRenderToolBar && { sx: { mb: 3 } })}
    />
  );

  useEffect(() => {
    if (groupingColDef && !!getTreeDataPath) setTreeDataState(true);
  }, [groupingColDef]);

  return (
    <GridContainer style={{ height: containerHeight || 'inherit' }}>
      {!shouldRenderToolBar ? (
        DataGridHeaderComponent
      ) : (
        <DataGridHeaderPortal>{DataGridHeaderComponent}</DataGridHeaderPortal>
      )}
      <GridStyleWrapper width="100%" height="inherit">
        <DataGridPro
          {...rest}
          disableSelectionOnClick={!!addRowDialog} // If not disabled this will interfere with inputs inside dialogs (e.g. VirtualizedAutoComplete)
          localeText={
            i18n.language.includes('nl')
              ? nlNL.components.MuiDataGrid.defaultProps.localeText
              : undefined
          }
          groupingColDef={groupingColDef}
          autoHeight={autoHeight}
          checkboxSelection={checkboxSelection && !disableActions}
          isRowSelectable={(params) => !params.row.disableSelection}
          rows={rows}
          columns={columnsToRender}
          components={{
            ...components,
            LoadingOverlay: LinearProgress,
            NoRowsOverlay: noRows
          }}
          selectionModel={customSelectionModel || selectionModel}
          onSelectionModelChange={(selectionModel) =>
            onSelectionModelChange(selectionModel)
          }
          loading={loading}
          treeData={treeDataState}
          getTreeDataPath={getTreeDataPath}
          initialState={initialState}
          pagination={pagination}
          rowsPerPageOptions={rowsPerPageOptions}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          rowHeight={rows.length === 0 ? 75 : 52} // If no rows set the height for the NoRowsOverlay
        />
        {showDeleteConfirmation && (
          <ConfirmationDialog
            handleAction={onDeleteConfirmation}
            cancelBtnText={t('no')}
            confirmBtnText={t('yes')}
            type="warning"
            title={confirmationTitle}
          >
            <p>{confirmationBody}</p>
          </ConfirmationDialog>
        )}
        {addRowDialog}
        {footer}
      </GridStyleWrapper>
    </GridContainer>
  );
};

export default CustomDataGrid;
